import { FC, useState, useEffect } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { MdWarning } from 'react-icons/md';
import BucketEntity from '../../../../domain/entities/BucketEntity';

interface BucketTreeNodeProps {
    bucket: BucketEntity;
    depth: number;
    selectedBucketId: number | null;
    onSelect: (bucket: BucketEntity) => void;
    searchTerm: string;
    isLast?: boolean;
}

const nodeMatchesSearch = (bucket: BucketEntity, term: string): boolean => {
    if (!term) return true;
    if (bucket.name.toLowerCase().includes(term)) return true;
    return bucket.children?.some(child => nodeMatchesSearch(child, term)) ?? false;
};

const BucketTreeNode: FC<BucketTreeNodeProps> = ({ bucket, depth, selectedBucketId, onSelect, searchTerm, isLast }) => {
    const hasChildren = bucket.children && bucket.children.length > 0;
    const [expanded, setExpanded] = useState<boolean>(depth < 1);

    const isSelected = selectedBucketId === bucket.bucket_id;
    const matchesSearch = nodeMatchesSearch(bucket, searchTerm);

    useEffect(() => {
        if (searchTerm && matchesSearch) {
            setExpanded(true);
        }
    }, [searchTerm, matchesSearch]);

    if (searchTerm && !matchesSearch) return null;

    const _handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    const _handleClick = () => {
        onSelect(bucket);
    };

    const depthClass = depth === 0 ? 'depth_root' : depth === 1 ? 'depth_location' : depth === 2 ? 'depth_client' : 'depth_supervisor';
    const badgeClass = bucket.user_count > 20 ? 'badge_high' : bucket.user_count > 5 ? 'badge_medium' : 'badge_low';

    return (
        <div className={`bucket_node ${depthClass}`}>
            <div
                className={`bucket_node_row ${isSelected ? 'bucket_node_selected' : ''}`}
                style={{ paddingLeft: `${depth * 20 + 12}px` }}
                onClick={_handleClick}
                title={`${bucket.name} — ${bucket.user_count} user(s)`}
            >
                {depth > 0 && (
                    <span className="bucket_node_connector">
                        <span className={`connector_line ${isLast ? 'connector_last' : ''}`} />
                    </span>
                )}

                <span className="bucket_node_toggle" onClick={_handleToggle}>
                    {hasChildren ? (
                        expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />
                    ) : <span className="toggle_spacer" />}
                </span>

                <span className="bucket_node_name">{bucket.name}</span>

                {bucket.user_count > 0 && (
                    <span className={`bucket_node_count ${badgeClass}`}>
                        {bucket.user_count}
                    </span>
                )}

                {bucket.hasWarning && (
                    <MdWarning className="bucket_node_warning" color="#e6a700" size={14} title="Bucket has issues" />
                )}
            </div>

            {hasChildren && expanded && (
                <div className="bucket_node_children">
                    {bucket.children!.map((child, index) => (
                        <BucketTreeNode
                            key={child.bucket_id}
                            bucket={child}
                            depth={depth + 1}
                            selectedBucketId={selectedBucketId}
                            onSelect={onSelect}
                            searchTerm={searchTerm}
                            isLast={index === bucket.children!.length - 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default BucketTreeNode;
